import metricMetadataIcon from '../assets/metric-metadata-icon-white.svg';
import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../utils/dashboardUtils";
import IconTooltip from "./IconTooltip";

interface DisaggregateMetricCardProps {
    id: number;
    title: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
};

const DisaggregateMetricCard = ({ id, title, disaggData, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: DisaggregateMetricCardProps) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    
    // const [stateData, setStateData] = useState<DisaggregatedMetric[] | null>(null);
    // const [nationalData, setNationalData] = useState<DisaggregatedMetric[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [edgeProximity, setEdgeProximity] = useState<{ top: boolean, right: boolean, bottom: boolean, left: boolean }>({
        top: false,
        right: false,
        bottom: false,
        left: false
    });

    const tooltipRef = useRef<HTMLDivElement>(null);

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);

        if (metadata && distributionData && countyDistributionData) {
            // Skip fetching if all data is already present
            return;
        }
        
        setLoading(true);

        const fetchMetadata = metadata ? Promise.resolve({ data: metadata }) : fetchMetricMetadata(id);
        const fetchDistData = distributionData ? Promise.resolve(distributionData) : fetchIndicatorBinCount(id, geoDetails, geoTag);
        const fetchCountyDist = countyDistributionData ? Promise.resolve(countyDistributionData) : fetchDistributionData(id, [], null);

        try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                fetchMetadata,
                fetchDistData,
                fetchCountyDist,
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

        } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`)); // Pass detailed error message
        } finally {
            setLoading(false);
        }

        /* *** Bypass queries for state and national data for now
        // Only proceed with state and national data fetch if the unit is 'percentage' or 'ratio'
        if ((unit === 'percentage' || unit === 'ratio')) {
            // Calculate geoPrefix if state data has not been fetched
            const geoPrefix = stateData ? null : calculateGeoPrefix(geoDetails);

            // Fetch state data if not already fetched
            if (!stateData && geoPrefix) {
                await fetchData(fetchStateData, setStateData, stateData, id, geoPrefix);
            }

            // Fetch national data if not already fetched
            if (!nationalData) {
                await fetchData(fetchNationalData, setNationalData, nationalData, id);
            }
        }
        */
    };

    useEffect(() => {
        if (tooltipRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const proximity = {
                top: tooltipRect.top < 30,
                right: window.innerWidth - tooltipRect.right < 90,
                bottom: window.innerHeight - tooltipRect.bottom < 200,
                left: tooltipRect.left < 50
            };
            setEdgeProximity(proximity);
        }
    }, []);

    return (
        <div className={`rounded-t-md bg-lacey-blue`}>
            <div className="flex items-center justify-between px-2 h-8">
                <p className="text-white text-sm/4 font-bold">{title}</p>
                {showMetadataIcon && 
                    <div ref={tooltipRef} className="h-4 w-3 mb-1">
                        <button onClick={handleClick}>
                            <img src={metricMetadataIcon} alt="Metric metadata icon"/>
                        </button>
                        <IconTooltip edgeProximity={edgeProximity}/>
                    </div>
                }
            </div>
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                disaggData={disaggData}
                stateData={null}
                nationalData={null}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                svg={svg}
                loading={loading}
                error={error}
            />
        </div>
    );
};


export default DisaggregateMetricCard;
